import React, { useState, useRef, useEffect } from 'react';
import { IoChevronDown, IoChevronUp, IoCheckmark } from 'react-icons/io5';
import { renderIcon } from '../utils/iconRenderer';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface CustomSelectProps {
  options: SelectOption[];
  value: string | undefined;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  required?: boolean;
  name?: string;
  id?: string;
  error?: boolean;
  errorMessage?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = '선택해주세요',
  disabled = false,
  className = '',
  required = false,
  name,
  id,
  error = false,
  errorMessage
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const selectRef = useRef<HTMLDivElement>(null);

  // 검색 기능 제거

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 키보드 네비게이션
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setHighlightedIndex(prev =>
            prev < options.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          setHighlightedIndex(prev =>
            prev > 0 ? prev - 1 : options.length - 1
          );
          break;
        case 'Enter':
          event.preventDefault();
          if (highlightedIndex >= 0 && options[highlightedIndex]) {
            handleOptionSelect(options[highlightedIndex]);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setHighlightedIndex(-1);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, highlightedIndex, options]);

  const handleToggle = () => {
    if (disabled) return;

    if (!isOpen) {
      setIsOpen(true);
      setHighlightedIndex(-1);
    } else {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }
  };

  const handleOptionSelect = (option: SelectOption) => {
    if (option.disabled) return;

    onChange(option.value);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  // 검색 기능 제거

  const selectedOption = options.find(option => option.value === value || value === '');

  return (
    <div
      ref={selectRef}
      className={`custom-select ${className} ${error ? 'error' : ''} ${disabled ? 'disabled' : ''}`}
      style={{ position: 'relative' }}
    >
      <div
        className={`select-trigger ${isOpen ? 'open' : ''}`}
        onClick={handleToggle}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-labelledby={id}
      >
        <div className="select-value">
          {selectedOption ? (
            <span className="selected-label">{selectedOption.label}</span>
          ) : (
            <span className="placeholder">{placeholder}</span>
          )}
        </div>
        <div className="select-arrow">
          {isOpen ? renderIcon(IoChevronUp, { size: 20 }) : renderIcon(IoChevronDown, { size: 20 })}
        </div>
      </div>

      {isOpen && (
        <div className="select-dropdown">
          <div className="options-list" role="listbox">
            {options.map((option, index) => (
              <div
                key={option.value}
                className={`option-item ${option.value === value ? 'selected' : ''} ${index === highlightedIndex ? 'highlighted' : ''} ${option.disabled ? 'disabled' : ''}`}
                onClick={() => handleOptionSelect(option)}
                role="option"
                aria-selected={option.value === value}
              >
                <span className="option-label">{option.label}</span>
                {option.value === value && (
                  renderIcon(IoCheckmark, { className: "check-icon", size: 18 })
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 숨겨진 select 요소 (폼 제출용) - visibility hidden으로 변경하여 required 검증 지원 */}
      <select
        name={name}
        id={id}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          top: 0,
          left: 0,
          opacity: 0,
          pointerEvents: 'none'
        }}
      >
        <option value="">{placeholder}</option>
        {options.map(option => (
          <option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </option>
        ))}
      </select>

      {error && errorMessage && (
        <div className="error-message">{errorMessage}</div>
      )}
    </div>
  );
};

export default CustomSelect; 